import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MessageSquare, Video, MapPin, Plus, ChevronDown, Reply, CheckCircle2, RotateCcw } from 'lucide-react';
import { api } from '../../../lib/api';
import FilterChip from '../../../components/crm/FilterChip';
import EmptyState from '../../../components/crm/EmptyState';
import InteractionLogger from '../../../components/crm/InteractionLogger';
import EmailComposer from '../../../components/admin/EmailComposer';
import ConversationThread, { Avatar, Pill, MessageCard } from '../../../components/admin/ConversationThread';
import Toast, { useToast } from '../../../components/admin/Toast';
import {
  relativeTime, messageTime, shortDate, firstName, shortName, avatarTone,
  channelLabel, isAutomatedMessage, mailboxChip, isResolved,
  buildFeed, FEED_FILTERS, feedMatches, feedCounts, announceNeedsReplyChange,
} from '../../../lib/communications';

// Re-exported so existing imports of the message card keep working.
export { MessageCard };

// Account → Communications (Phase 1). Everything anyone at NexSav has said
// to this school and what came back: email conversations from connected
// Gmail mailboxes and CRM sends, merged with the calls and visits logged on
// the timeline. Amber = the school wrote last and nobody has answered.
export default function CommunicationsTab({ account, onSummary }) {
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [staffNames, setStaffNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(() => new Set());
  const [loggerOpen, setLoggerOpen] = useState(false);
  const [composer, setComposer] = useState(null); // { mode: 'compose' } | { mode: 'reply', conversation }
  const [busy, setBusy] = useState({}); // conversationId → 'resolve' | 'responsible'
  const [staff, setStaff] = useState([]);
  const [toast, setToast] = useToast();

  const load = useCallback(async ({ sync = true } = {}) => {
    setLoading(true);
    setError('');
    try {
      const [comms, tl, boxes] = await Promise.all([
        api.get(`/api/admin/accounts/${account.id}/communications?limit=30${sync ? '' : '&sync=0'}`),
        api.get(`/api/admin/accounts/${account.id}/timeline`).catch(() => ({ timeline: [] })),
        api.get('/api/admin/communications/mailboxes').catch(() => null),
      ]);
      setData(comms || null);
      setTimeline(tl?.timeline || []);
      const names = {};
      for (const s of boxes?.staff || []) names[s.id] = s.name;
      setStaffNames(names);
      setStaff((boxes?.staff || []).map((s) => ({ id: s.id, name: s.name, email: s.email, role: s.role })));
      onSummary?.(comms?.summary || null);
    } catch (err) {
      setError(err?.message || 'Failed to load communications');
    } finally {
      setLoading(false);
    }
  }, [account.id, onSummary]);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary || null;
  const feed = useMemo(() => buildFeed(data?.conversations || [], timeline), [data, timeline]);
  const counts = useMemo(() => feedCounts(feed), [feed]);
  const visible = feed.filter((i) => feedMatches(i, filter));

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const noMailbox = summary?.sync ? Number(summary.sync.connected) === 0 : false;

  const setConvBusy = (id, what) => setBusy((b) => ({ ...b, [id]: what }));

  const toggleResolved = async (c) => {
    const resolved = isResolved(c);
    setConvBusy(c.id, 'resolve');
    try {
      if (resolved) await api.delete(`/api/admin/communications/conversations/${c.id}/resolve`);
      else await api.post(`/api/admin/communications/conversations/${c.id}/resolve`, {});
      setToast({ message: resolved ? 'Conversation reopened' : 'Marked resolved', detail: resolved ? undefined : 'It no longer counts as waiting on a reply.' });
      announceNeedsReplyChange();
      await load({ sync: false });
    } catch (err) {
      setToast({ message: resolved ? 'Couldn’t reopen' : 'Couldn’t mark resolved', detail: err?.message });
    } finally {
      setConvBusy(c.id, null);
    }
  };

  const setResponsible = async (c, userId) => {
    setConvBusy(c.id, 'responsible');
    try {
      const res = await api.put(`/api/admin/communications/conversations/${c.id}/responsible`, { user_id: userId || null });
      setToast({ message: res?.responsible ? `${firstName(res.responsible.name)} is now responsible` : 'Responsible cleared' });
      announceNeedsReplyChange();
      await load({ sync: false });
    } catch (err) {
      setToast({ message: 'Couldn’t reassign', detail: err?.message });
    } finally {
      setConvBusy(c.id, null);
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <p className="text-[12px] text-gray-500 max-w-xl">
          Every email, call, and visit with this school, newest first. Amber means the school wrote last and no one has answered.
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setLoggerOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] border border-white/[0.08] bg-white/[0.02] text-gray-200 hover:text-white hover:border-white/[0.15] transition-colors"
          >
            <Plus className="w-3 h-3" /> Log call or visit
          </button>
          <button
            type="button"
            onClick={() => setComposer({ mode: 'compose' })}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900 transition-colors"
            data-testid="new-email"
          >
            <Mail className="w-3 h-3" /> New email
          </button>
        </div>
      </div>

      {error && (
        <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4">{error}</div>
      )}

      {data?.account?.outreachOptedOutAt && (
        <div className="mb-4 px-3 py-2 rounded-lg bg-amber-500/[0.08] border border-amber-500/25 text-[12px] text-amber-200/90">
          This school opted out of automated outreach on {shortDate(data.account.outreachOptedOutAt)}. Campaign emails are paused; one-to-one replies still go through.
        </div>
      )}

      {loading && !data ? (
        <div className="animate-pulse space-y-3">
          <div className="h-20 rounded-xl bg-white/[0.03]" />
          <div className="h-14 rounded-xl bg-white/[0.03]" />
          <div className="h-14 rounded-xl bg-white/[0.03]" />
        </div>
      ) : (
        <>
          {summary && <SummaryStrip summary={summary} />}
          {data?.scoped && <p className="text-[12px] text-gray-500 -mt-2 mb-4" data-testid="scope-note">Showing the conversations your own mailbox took part in, plus any you’re responsible for. Admins see everyone’s.</p>}

          {feed.length === 0 ? (
            <EmptyState
              icon={Mail}
              title="No email on record for this school yet."
              description={noMailbox
                ? 'Connect a Gmail mailbox and history with this school’s contacts imports automatically.'
                : 'Emails with this school’s contacts appear here as they sync. Calls and visits you log show up too.'}
              action={noMailbox ? (
                <Link
                  to="/admin/communications"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900"
                >
                  <Mail className="w-3 h-3" /> Connect Gmail
                </Link>
              ) : null}
            />
          ) : (
            <>
              <div className="flex items-center gap-1.5 flex-wrap mb-3" role="group" aria-label="Filter communications">
                {FEED_FILTERS.map(([key, label]) => (
                  <FilterChip key={key} active={filter === key} onClick={() => setFilter(key)} aria-pressed={filter === key}>
                    {label} <span className="opacity-60 tabular-nums">{counts[key] ?? 0}</span>
                  </FilterChip>
                ))}
              </div>

              {visible.length === 0 ? (
                <EmptyState icon={Mail} title="Nothing matches this filter" />
              ) : (
                <ul className="space-y-2 list-none m-0 p-0">
                  {visible.map((item) => (
                    <li key={item.id}>
                      {item.kind === 'conversation' ? (
                        <ConversationRow
                          conversation={item.conversation}
                          expanded={expanded.has(item.id)}
                          onToggle={() => toggle(item.id)}
                          staff={staff}
                          busy={busy[item.conversation.id]}
                          onReply={() => setComposer({ mode: 'reply', conversation: item.conversation })}
                          onToggleResolved={() => toggleResolved(item.conversation)}
                          onSetResponsible={(uid) => setResponsible(item.conversation, uid)}
                        />
                      ) : (
                        <InteractionRow interaction={item.interaction} at={item.at} staffNames={staffNames} />
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </>
      )}

      {loggerOpen && (
        <InteractionLogger
          open
          accountId={account.id}
          title="Log call or visit"
          onClose={() => setLoggerOpen(false)}
          onLogged={() => { setLoggerOpen(false); load({ sync: false }); }}
        />
      )}

      {composer && (
        <EmailComposer
          mode={composer.mode}
          conversation={composer.conversation || null}
          accountId={account.id}
          accountName={account.name}
          account={data?.account || account}
          onClose={() => setComposer(null)}
          onToast={setToast}
          onSent={() => { setComposer(null); load({ sync: false }); }}
        />
      )}

      <Toast toast={toast} />
    </div>
  );
}

// ---------------------------------------------------------------------------

export function SummaryStrip({ summary }) {
  const lc = summary.lastContact;
  const lr = summary.lastReply;
  const needs = Number(summary.needsReply) || 0;
  const resp = summary.responsible;
  const next = summary.nextAutomated;
  const sync = summary.sync || {};

  let syncCell;
  if (Number(sync.connected) === 0) {
    syncCell = (
      <>
        <span className="w-[7px] h-[7px] rounded-full bg-gray-600 inline-block" aria-hidden="true" />
        <span className="text-gray-400 font-normal">No mailbox connected</span>
        <Link to="/admin/communications" className="text-[11px] text-[#B0FC38] hover:underline font-normal">Connect</Link>
      </>
    );
  } else if (Number(sync.errored) > 0) {
    syncCell = (
      <>
        <span className="w-[7px] h-[7px] rounded-full bg-red-400 inline-block" aria-hidden="true" />
        <span className="text-red-300">Connection problem</span>
        <Link to="/admin/communications" className="text-[11px] text-gray-500 hover:text-white font-normal">Fix</Link>
      </>
    );
  } else if (Number(sync.importing) > 0) {
    syncCell = (
      <>
        <span className="w-[7px] h-[7px] rounded-full bg-amber-400 inline-block animate-pulse" aria-hidden="true" />
        <span className="text-amber-300">Importing…</span>
      </>
    );
  } else {
    syncCell = (
      <>
        <span className="w-[7px] h-[7px] rounded-full bg-[#B0FC38] inline-block" aria-hidden="true" />
        <span className="tabular-nums">{sync.lastSyncedAt ? relativeTime(sync.lastSyncedAt) : 'Not synced yet'}</span>
      </>
    );
  }

  return (
    <dl className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-white/[0.06] border border-white/[0.06] rounded-xl overflow-hidden mb-5" data-testid="comms-summary">
      <Cell label="Last contact">
        {lc ? (
          <>
            <span className="tabular-nums">{relativeTime(lc.at)}</span>
            <small className="font-normal text-gray-500 text-[11px]">
              {[firstName(lc.by), lc.channel === 'email' ? 'email' : channelLabel(lc.channel).toLowerCase()].filter(Boolean).join(' · ')}
            </small>
          </>
        ) : <span className="text-gray-500 font-normal">None yet</span>}
      </Cell>
      <Cell label="Last reply from school">
        {lr ? (
          <>
            <span className={`tabular-nums ${needs > 0 ? 'text-amber-300' : ''}`}>{relativeTime(lr.at)}</span>
            <small className="font-normal text-gray-500 text-[11px] truncate">{shortName(lr.from)}</small>
            {needs > 0 && <Pill tone="amber">Needs reply</Pill>}
          </>
        ) : <span className="text-gray-500 font-normal">None yet</span>}
      </Cell>
      <Cell label="Responsible">
        {resp ? (
          <>
            <Avatar label={resp.name} className={avatarTone(resp.name)} size="sm" />
            <span>{firstName(resp.name) || resp.name}</span>
          </>
        ) : <span className="text-gray-500 font-normal">Unassigned</span>}
      </Cell>
      <Cell label="Next automated email">
        {next && next.at ? (
          <>
            <span className="tabular-nums">{shortDate(next.at)}</span>
            <small className="font-normal text-gray-500 text-[11px] truncate">{next.campaign}</small>
          </>
        ) : next && next.paused ? (
          <>
            <span>None</span>
            <small className="font-normal text-gray-500 text-[11px]">paused — school replied</small>
          </>
        ) : <span>None</span>}
      </Cell>
      <Cell label="Mailbox sync">{syncCell}</Cell>
    </dl>
  );
}

function Cell({ label, children }) {
  return (
    <div className="bg-[#0f0f16] px-4 py-3 min-w-0">
      <dt className="text-[10px] uppercase tracking-wider text-gray-500">{label}</dt>
      <dd className="m-0 mt-1 text-[14px] font-semibold text-white flex items-center gap-2 flex-wrap min-w-0">{children}</dd>
    </div>
  );
}

// ---------------------------------------------------------------------------

function ConversationRow({ conversation: c, expanded, onToggle, staff = [], busy = null, onReply, onToggleResolved, onSetResponsible }) {
  // The list arrives without bodies (a busy school can hold megabytes of
  // email HTML). The first expand fetches this thread's full messages.
  const [full, setFull] = useState(null);
  const [loadingFull, setLoadingFull] = useState(false);
  const inFlight = useRef(false);
  const listMsgs = c.messages || [];
  const needsBodies = listMsgs.some((m) => m.bodyText === undefined && m.bodyHtml === undefined);
  useEffect(() => {
    if (!expanded || !needsBodies || full || inFlight.current) return undefined;
    let alive = true;
    inFlight.current = true;
    setLoadingFull(true);
    api.get(`/api/admin/communications/conversations/${c.id}`)
      .then((res) => { if (alive && res?.conversation?.messages) setFull(res.conversation.messages); })
      .catch(() => {})
      .finally(() => { inFlight.current = false; if (alive) setLoadingFull(false); });
    return () => { alive = false; };
  }, [expanded, needsBodies, full, c.id]);
  // Bust the cache when the list's message count changes (a reply landed).
  useEffect(() => { setFull(null); }, [listMsgs.length]);
  const msgs = full || listMsgs;
  const last = msgs[msgs.length - 1];
  const lastFromLabel = last ? (last.direction === 'outbound'
    ? (firstName(last.sentByName) || firstName(last.staffName) || last.from?.name || last.from?.email)
    : shortName(last.from?.name, last.from?.email)) : '';
  const chip = mailboxChip(c);
  const inboundLatest = !!c.needsReply;
  const panelId = `conv-${c.id}-messages`;
  const isAutomatedOnly = msgs.length > 0 && msgs.every(isAutomatedMessage);

  return (
    <div
      className={`rounded-xl border bg-white/[0.02] overflow-hidden ${inboundLatest ? 'border-amber-500/40 border-l-2 border-l-amber-400' : 'border-white/[0.06]'}`}
      data-testid="conversation-row"
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={panelId}
        className="w-full text-left grid grid-cols-[28px_1fr_auto] gap-3 items-center px-3.5 py-3 hover:bg-white/[0.03] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#B0FC38] focus-visible:-outline-offset-2"
      >
        {isAutomatedOnly ? (
          <span aria-hidden="true" className="w-7 h-7 rounded-full inline-grid place-items-center bg-[#1b1b24] text-gray-500 shrink-0"><Mail className="w-3.5 h-3.5" /></span>
        ) : (
          <Avatar label={last?.from?.name || last?.from?.email || c.subject} className={avatarTone(last?.from?.email || last?.from?.name)} />
        )}
        <span className="min-w-0 flex flex-col">
          <span className="flex items-center gap-2 min-w-0">
            <span className="text-[13px] font-semibold text-white truncate">{c.subject || '(no subject)'}</span>
            {c.linkedRequest && <Pill tone="sky">{c.linkedRequest.kind === 'ticket' ? 'Ticket' : 'Edit request'}</Pill>}
            {c.needsReply && <Pill tone="amber">Needs reply</Pill>}
          </span>
          {last && (
            <span className="text-[12px] text-gray-500 truncate mt-0.5">
              {lastFromLabel && <span className="text-gray-300">{lastFromLabel}: </span>}
              {last.snippet || last.bodyText?.slice(0, 160) || ''}
            </span>
          )}
        </span>
        <span className="text-right text-[11px] text-gray-500 flex flex-col items-end gap-1">
          <span className="tabular-nums whitespace-nowrap">{messageTime(c.lastMessageAt)}</span>
          <span className="flex items-center gap-2">
            {chip && <span className="px-2 py-0.5 rounded-full border border-white/[0.08] text-gray-400 whitespace-nowrap">{chip}</span>}
            <span className="whitespace-nowrap">{c.messageCount || msgs.length} {Number(c.messageCount || msgs.length) === 1 ? 'message' : 'messages'}</span>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} aria-hidden="true" />
          </span>
        </span>
      </button>
      <div id={panelId} hidden={!expanded} className="border-t border-white/[0.06] px-3.5 pt-2 pb-3.5">
        {expanded && loadingFull && !full && (
          <div className="py-3 text-[12px] text-gray-500" data-testid="thread-loading">Loading messages…</div>
        )}
        {expanded && <ConversationThread messages={msgs} />}
        {expanded && c.linkedRequest && (
          <div className="mt-3 pt-3 border-t border-white/[0.06] flex items-center justify-between gap-3 flex-wrap text-[12px] text-gray-400" data-testid="linked-request">
            <span>
              This thread belongs to the {c.linkedRequest.kind === 'ticket' ? 'ticket' : 'edit request'}{' '}
              <span className="text-gray-200">“{c.linkedRequest.title}”</span>. Answer it there so its status stays in step.
            </span>
            <Link to={c.linkedRequest.href} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-sky-400/35 text-sky-300 hover:text-white hover:border-sky-300 transition-colors">
              Open {c.linkedRequest.kind === 'ticket' ? 'ticket' : 'edit request'}
            </Link>
          </div>
        )}
        {expanded && !c.linkedRequest && (
          <ConversationActions
            conversation={c}
            staff={staff}
            busy={busy}
            onReply={onReply}
            onToggleResolved={onToggleResolved}
            onSetResponsible={onSetResponsible}
          />
        )}
      </div>
    </div>
  );
}

// Reply · Mark resolved / Reopen · Responsible ▾ — under the thread once a
// conversation is expanded. Resolved wins only when it happened after the
// school's latest human message (same rule the server uses for needsReply).
export function ConversationActions({ conversation: c, staff = [], busy = null, onReply, onToggleResolved, onSetResponsible, className = '' }) {
  const resolved = isResolved(c);
  return (
    <div className={`mt-3 pt-3 border-t border-white/[0.06] flex items-center gap-2 flex-wrap ${className}`} data-testid="conversation-actions">
      <button
        type="button"
        onClick={onReply}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900 transition-colors"
      >
        <Reply className="w-3 h-3" aria-hidden="true" /> Reply
      </button>
      <button
        type="button"
        onClick={onToggleResolved}
        disabled={!!busy}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] border border-white/[0.08] bg-white/[0.02] text-gray-200 hover:text-white hover:border-white/[0.15] transition-colors disabled:opacity-50"
      >
        {resolved ? <RotateCcw className="w-3 h-3" aria-hidden="true" /> : <CheckCircle2 className="w-3 h-3" aria-hidden="true" />}
        {busy === 'resolve' ? 'Saving…' : resolved ? 'Reopen' : 'Mark resolved'}
      </button>
      <ResponsibleMenu
        value={c.responsibleUserId}
        name={c.responsibleName}
        staff={staff}
        busy={busy === 'responsible'}
        onChange={onSetResponsible}
        className="ml-auto"
      />
    </div>
  );
}

export function ResponsibleMenu({ value, name, staff = [], busy = false, onChange, className = '', label = 'Responsible' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const shown = name || staff.find((s) => s.id === value)?.name || '';
  return (
    <div className={`relative ${className}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={busy}
        data-testid="responsible-menu"
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] border border-white/[0.08] bg-white/[0.02] text-gray-300 hover:text-white hover:border-white/[0.15] transition-colors disabled:opacity-50"
      >
        {shown && <Avatar label={shown} className={avatarTone(shown)} size="sm" />}
        <span>{label}: <span className="text-white">{busy ? 'Saving…' : (shown ? firstName(shown) : 'Unassigned')}</span></span>
        <ChevronDown className="w-3 h-3 text-gray-500" aria-hidden="true" />
      </button>
      {open && (
        <div role="menu" aria-label="Choose who is responsible" className="absolute right-0 top-full mt-1.5 z-20 min-w-[200px] rounded-xl border border-white/[0.1] bg-[#0f0f16] shadow-[0_20px_60px_-10px_rgba(0,0,0,0.8)] p-1.5">
          {staff.map((s) => (
            <button
              key={s.id}
              type="button"
              role="menuitemradio"
              aria-checked={s.id === value}
              onClick={() => { setOpen(false); if (s.id !== value) onChange(s.id); }}
              className={`w-full text-left flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[12.5px] ${s.id === value ? 'bg-[#B0FC38]/[0.12] text-white' : 'text-gray-200 hover:bg-white/[0.04]'}`}
            >
              <Avatar label={s.name} className={avatarTone(s.name)} size="sm" />
              <span className="flex-1 truncate">{s.name}</span>
            </button>
          ))}
          {staff.length === 0 && <div className="px-2.5 py-1.5 text-[12px] text-gray-500">No staff loaded.</div>}
          <div className="my-1 border-t border-white/[0.06]" />
          <button
            type="button"
            role="menuitemradio"
            aria-checked={!value}
            onClick={() => { setOpen(false); if (value) onChange(null); }}
            className="w-full text-left px-2.5 py-1.5 rounded-lg text-[12.5px] text-gray-400 hover:text-white hover:bg-white/[0.04]"
          >
            Unassigned
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

const CHANNEL_ICONS = { phone: Phone, in_person: MapPin, sms: MessageSquare, video_call: Video };

function InteractionRow({ interaction: it, at, staffNames }) {
  const name = staffNames[it.userId] || '';
  const label = channelLabel(it.channel);
  const Icon = CHANNEL_ICONS[it.channel] || MessageSquare;
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] grid grid-cols-[28px_1fr_auto] gap-3 items-center px-3.5 py-3" data-testid="interaction-row">
      {name ? (
        <Avatar label={name} className={avatarTone(name)} />
      ) : (
        <span aria-hidden="true" className="w-7 h-7 rounded-full inline-grid place-items-center bg-white/[0.04] border border-white/[0.06] text-gray-400 shrink-0"><Icon className="w-3.5 h-3.5" /></span>
      )}
      <span className="min-w-0 flex flex-col">
        <span className="flex items-center gap-2 min-w-0">
          <span className="text-[13px] font-semibold text-white truncate">
            {label}{it.subject ? ` · ${it.subject}` : ''}
          </span>
          <Pill>{it.channel === 'in_person' ? 'Visit' : it.channel === 'phone' ? 'Call' : label}</Pill>
        </span>
        {it.summary && (
          <span className="text-[12px] text-gray-500 truncate mt-0.5">
            {name && <span className="text-gray-300">{firstName(name)}: </span>}
            {it.summary}
          </span>
        )}
        {it.followUpNeeded && (
          <span className="text-[11px] text-amber-300 mt-0.5">Follow-up: {it.followUpNotes || 'needed'}</span>
        )}
      </span>
      <span className="text-right text-[11px] text-gray-500 flex flex-col items-end gap-1">
        <span className="tabular-nums whitespace-nowrap">{messageTime(it.occurredAt || at)}</span>
        <span>Logged manually</span>
      </span>
    </div>
  );
}
